<!-- Candidate Booking Component -->
<div class="candidate-booking-container">
  
  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="spinner"></div>
    <p>Loading meeting details...</p>
  </div>
  
  <!-- Error State -->
  <div *ngIf="error && !loading" class="error-state">
    <h2>Unable to load meeting</h2>
    <p>{{ error }}</p>
    <button class="retry-btn" (click)="loadMeetingEvent()">Try Again</button>
  </div>
  
  <!-- Success State -->
  <div *ngIf="bookingSuccess" class="success-state">
    <h2>Your meeting has been scheduled!</h2>
    <div class="booking-details">
      <h3>Meeting Details</h3>
      <p><strong>Date:</strong> {{ selectedDate | date:'EEEE, MMMM d, yyyy' }}</p>
      <p><strong>Time:</strong> {{ formatTime(selectedTimeSlot!.start_time) }} - {{ formatTime(selectedTimeSlot!.end_time) }}</p>
      <p><strong>Duration:</strong> {{ meetingEvent!.duration }} minutes</p>
      <p><strong>Location:</strong> {{ getLocationLabel() }}</p>
      <p><strong>With:</strong> {{ meetingEvent!.user_id.name }}</p>
    </div>
    
    <div class="next-steps">
      <h3>Next Steps:</h3>
      <ul>
        <li>You will receive a confirmation email shortly</li>
        <li>The meeting has been added to your calendar</li>
        <li>You'll receive a reminder before the meeting</li>
        <li>Questions? Reply to the confirmation email</li>
      </ul>
    </div>
  </div>
  
  <!-- Time Selection View (Image #3) -->
  <div *ngIf="meetingEvent && !showBookingForm && !bookingSuccess" class="time-selection-view">
    
    <div class="booking-container">
      
      <!-- Time Selection Panel -->
      <div class="time-selection-panel">
        <div class="panel-header">
          <h2>Select a Date & Time</h2>
          
          <!-- Timezone Selector -->
          <div class="timezone-selector">
            <select [(ngModel)]="selectedTimezone">
              <option *ngFor="let tz of timezoneOptions" [value]="tz.value">
                {{ tz.label }}
              </option>
            </select>
          </div>
        </div>
        
        <div class="calendar-section">
          
          <!-- Available Dates Calendar -->
          <div class="available-dates">
            <h3>Available Dates</h3>
            
            <!-- Month Navigation -->
            <div class="month-nav">
              <button (click)="previousMonth()" class="nav-btn">&larr;</button>
              <span class="month-year">{{ currentMonth | date:'MMM yyyy' }}</span>
              <button (click)="nextMonth()" class="nav-btn">&rarr;</button>
            </div>
            
            <div class="mini-calendar">
              <div class="calendar-grid">
                <div class="day-header">Sun</div>
                <div class="day-header">Mon</div>
                <div class="day-header">Tue</div>
                <div class="day-header">Wed</div>
                <div class="day-header">Thu</div>
                <div class="day-header">Fri</div>
                <div class="day-header">Sat</div>
                
                <div *ngFor="let day of calendarDays"
                     class="calendar-day"
                     [class.available]="day.isAvailable"
                     [class.selected]="day.isSelected"
                     [class.other-month]="!day.isCurrentMonth"
                     [class.today]="day.isToday"
                     (click)="onDateClick(day.date)">
                  {{ day.date.getDate() }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Available Times -->
          <div class="available-times">
            <h3 *ngIf="selectedDate">
              Available Times - {{ selectedDate | date:'EEEE, MMMM d, yyyy' }}
            </h3>
            
            <div class="time-slots" *ngIf="availableTimesForDate.length > 0">
              <div *ngFor="let slot of availableTimesForDate"
                   class="time-slot"
                   [class.selected]="selectedTimeSlot === slot"
                   (click)="onTimeSlotClick(slot)">
                <div class="time">{{ formatTime(slot.start_time) }}</div>
                <div class="duration">{{ meetingEvent.duration }} min</div>
              </div>
            </div>
            
            <div class="no-times" *ngIf="selectedDate && availableTimesForDate.length === 0">
              No available times for this date
            </div>
            
            <div class="select-date" *ngIf="!selectedDate">
              Please select a date to see available times
            </div>
          </div>
          
        </div>
      </div>
      
      <!-- Meeting Details Sidebar -->
      <div class="meeting-details-panel">
        <h3>Meeting Details</h3>
        
        <div class="detail-item">
          <div class="icon">🕐</div>
          <div class="content">
            <div class="title">{{ meetingEvent.duration }} minutes</div>
            <div class="subtitle">Duration</div>
          </div>
        </div>
        
        <div class="detail-item">
          <div class="icon">📹</div>
          <div class="content">
            <div class="title">{{ getLocationTypeLabel() }}</div>
            <div class="subtitle">Meeting type</div>
          </div>
        </div>
        
        <div class="detail-item">
          <div class="icon">📍</div>
          <div class="content">
            <div class="title">{{ getLocationLabel() }}</div>
            <div class="subtitle">Location</div>
          </div>
        </div>
        
        <!-- Selected Time Display -->
        <div class="selected-time" *ngIf="selectedTimeSlot">
          <h4>Selected Time</h4>
          <div class="selected-details">
            <div class="date">{{ selectedDate | date:'EEEE, MMMM d, yyyy' }}</div>
            <div class="time">{{ formatTime(selectedTimeSlot.start_time) }} - {{ selectedTimezone }}</div>
          </div>
          
          <button class="continue-btn" 
                  (click)="continueBooking()">
            Continue Booking
          </button>
        </div>
      </div>
      
    </div>
  </div>
  
  <!-- Booking Form View (Image #4) -->
  <div *ngIf="showBookingForm && !bookingSuccess" class="booking-form-view">
    
    <div class="form-container">
      
      <div class="form-header">
        <h2>Complete Your Booking</h2>
        <div class="selected-time-summary">
          <div class="date">{{ selectedDate | date:'EEEE, MMMM d, yyyy' }}</div>
          <div class="time">{{ formatTime(selectedTimeSlot!.start_time) }} ({{ meetingEvent!.duration }} minutes)</div>
        </div>
      </div>
      
      <form class="booking-details-form" (ngSubmit)="scheduleMeeting()">
        
        <!-- Full Name -->
        <div class="form-group">
          <label for="candidateName">Full Name *</label>
          <input type="text" 
                 id="candidateName"
                 [(ngModel)]="candidateName"
                 name="candidateName"
                 placeholder="Enter your full name"
                 required
                 class="form-input">
        </div>
        
        <!-- Email Address -->
        <div class="form-group">
          <label for="candidateEmail">Email Address *</label>
          <input type="email"
                 id="candidateEmail"
                 [(ngModel)]="candidateEmail"
                 name="candidateEmail"
                 placeholder="Enter your email"
                 required
                 (blur)="validateEmail()"
                 (input)="emailError = ''"
                 class="form-input"
                 [class.input-error]="emailError">
          <span *ngIf="emailError" class="field-error">{{ emailError }}</span>
        </div>
        
        <!-- Phone Number -->
        <div class="form-group">
          <label for="candidatePhone">Phone Number</label>
          <input type="tel" 
                 id="candidatePhone"
                 [(ngModel)]="candidatePhone"
                 name="candidatePhone"
                 placeholder="Enter your phone number"
                 class="form-input">
        </div>
        
        <!-- Additional Notes -->
        <div class="form-group">
          <label for="additionalNotes">Additional Notes</label>
          <textarea id="additionalNotes"
                    [(ngModel)]="additionalNotes"
                    name="additionalNotes"
                    placeholder="Any additional information or questions..."
                    class="form-textarea"
                    rows="4">
          </textarea>
        </div>
        
        <!-- Form Actions -->
        <div class="form-actions">
          <button type="button" 
                  class="back-btn" 
                  (click)="goBackToTimeSelection()">
            Back
          </button>
          <button type="submit" 
                  class="schedule-btn"
                  [disabled]="!candidateName || !candidateEmail || isSubmitting">
            <span *ngIf="!isSubmitting">Schedule Meeting</span>
            <span *ngIf="isSubmitting">Scheduling...</span>
          </button>
        </div>
        
      </form>
      
    </div>
  </div>
  
</div>